import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { upsertProfileAttribute, fetchProfileCompletion, fetchProfileAttributes, fetchMe, fetchGeoStatus, isAccountIneligible } from '../lib/api';
import { supabase } from '../lib/supabase';
import type { ProfileAttributeKey } from '@swipeopinion/shared';

export function useMe(enabled = true) {
  return useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    enabled,
    // Don't retry the ejected-member 403 (ACCOUNT_INELIGIBLE) — it's a stable
    // dead-end, so retrying 3× just delays the care screen. Other errors keep
    // the default 3 retries.
    retry: (count, error) => !isAccountIneligible(error) && count < 3,
  });
}

export function useGeoStatus(enabled = true) {
  return useQuery({
    queryKey: ['geo-status'],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Not authenticated');
      return fetchGeoStatus(token);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useProfileCompletion() {
  const { isSuccess: meReady } = useMe();
  return useQuery({
    queryKey: ['profile', 'completion'],
    queryFn: fetchProfileCompletion,
    enabled: meReady,
  });
}

export function useProfileAttributes() {
  const { isSuccess: meReady } = useMe();
  return useQuery({
    queryKey: ['profile', 'attributes'],
    queryFn: fetchProfileAttributes,
    enabled: meReady,
  });
}

export function useUpsertAttribute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: ProfileAttributeKey; value: string }) =>
      upsertProfileAttribute({ key, value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['profile', 'completion'] });
      void queryClient.invalidateQueries({ queryKey: ['profile', 'attributes'] });
      // The score banner (ProfilePage) + dashboard ProfileScoreCard both read
      // ['profile','score']; without this they'd show a stale number after a save
      // while the ring (['profile','completion']) refetched — the banner-vs-ring
      // divergence. Invalidate all three so every figure refetches together.
      void queryClient.invalidateQueries({ queryKey: ['profile', 'score'] });
    },
  });
}
